import { SearchOutlined } from "@ant-design/icons";
import { Col, Input, Row, Typography, Form, Select } from "antd";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  AppDispatch,
  dynamicSet,
  getChildOrganizationList,
  getChildOrganizationListRequest,
  getOrganizationRequest,
  useDynamicSelector,
  type getOrganization,
} from "@src/redux";
import { filterOptions } from "@src/helpers/constants";
import { useParams } from "react-router-dom";

const { Title, Text } = Typography;

const OrganizationFilterForm: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { id: organizationId } = useParams();
  const [form] = Form.useForm();

  const isArchived = Form.useWatch("isArchived", form);

  const {
    result: childOrganizationListResult,
    loading: childOrganizationListLoading,
  } = useSelector(getChildOrganizationList);
  const { filter: savedFilter } = useDynamicSelector("organization_filter");
  const { pagination: organizationPagination } = useDynamicSelector(
    "organization_pagination"
  );

  const [searchValue, setSearchValue] = useState(
    savedFilter?.searchString || ""
  );
  const hasInitialized = useRef(false);
  const initialPagination = { pageLimit: 10, pageNumber: 1 };

  useEffect(() => {
    form.setFieldsValue({
      search: savedFilter?.searchString || "",
      isArchived: savedFilter?.status ?? "all",
    });
    setSearchValue(savedFilter?.searchString || "");
  }, [form, savedFilter]);

  const normalizeStatus = (value?: string) =>
    !value || value === "all" ? undefined : value;

  const buildFilters = (searchInput: string): any => ({
    parentOrganizationId: organizationId || "",
    searchString: searchInput,
    status: normalizeStatus(isArchived),
  });

  const requestList = (
    filters: any,
    pagination: { pageLimit: number; pageNumber: number }
  ) => {
    dispatch(dynamicSet("organization_filter", { filter: filters }));
    dispatch(dynamicSet("organization_pagination", { pagination }));
    dispatch(
      getChildOrganizationListRequest({
        filter: filters,
        pagination,
      })
    );
  };

  const triggerSearch = (searchInput: string) => {
    requestList(buildFilters(searchInput), initialPagination);
  };

  useEffect(() => {
    if (hasInitialized.current) return;
    hasInitialized.current = true;
    const pagination =
      organizationPagination ||
      childOrganizationListResult?.pagination ||
      initialPagination;
    requestList(buildFilters(savedFilter?.searchString || ""), pagination);
  }, [
    organizationPagination,
    childOrganizationListResult,
    savedFilter,
    organizationId,
  ]);

  useEffect(() => {
    if (!hasInitialized.current) return;
    requestList(buildFilters(searchValue), initialPagination);
  }, [isArchived, organizationId]);

  useEffect(() => {
    if (organizationId) {
      dispatch(getOrganizationRequest({ filter: { id: organizationId } }));
    }
  }, [organizationId]);

  // Form change handler (not used, but kept for structure)
  const handleFormChange = (changedValues: any, allValues: any) => {};

  return (
    <div className="filter-container">
      <div>
        <Row className="header-filter" align="middle">
          <Col span={24}>
            <Title level={4} className="client-filters-header-title">
              Filters
            </Title>
          </Col>
        </Row>
        <Form
          layout="vertical"
          onValuesChange={handleFormChange}
          className="common-filters"
          form={form}
        >
          <Row className="filter-row" gutter={[16, 16]}>
            <Col span={24}>
              <Form.Item label="Search" className="filter-label" name="search">
                <Input
                  prefix={<SearchOutlined />}
                  className="filter-input"
                  allowClear
                  value={searchValue}
                  onChange={e => {
                    setSearchValue(e.target.value);
                    if (e.target.value === "") {
                      form.setFieldsValue({ search: "" });
                      triggerSearch(""); // AllowClear triggers API
                    }
                  }}
                  onPressEnter={e => {
                    form.setFieldsValue({ search: searchValue });
                    triggerSearch(searchValue); // Enter triggers API
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item label="Status" name="isArchived">
                <Select
                  defaultValue="all"
                  className="filter-select"
                  options={filterOptions.subjectStatus}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </div>
      <Row className="footer">
        <Col>
          <Text strong style={{ fontSize: "20px" }}>
            Total
          </Text>
        </Col>
        <Col className="total-value">
          <Text strong style={{ color: "#ffffff", fontSize: "17px" }}>
            {childOrganizationListResult?.pagination?.totalCount || 0}
          </Text>
        </Col>
      </Row>
    </div>
  );
};

export default OrganizationFilterForm;
